import { Box, Grid, Stack, Typography, Button, TextField, Divider, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { Iconify, NextImage } from '@sdf/base';
import { resetAuthSession } from '../../utils/authReset';
import { SxProps, useMediaQuery } from '@mui/system';
import { loginStyle as style } from '../../styles';
import { Styles } from '@t2c/shared-fe';
import { LOGIN_SCREEN_VIDEO, UPLOADED_PUBLIC_ASSETS_URL } from '../../utils/constants';

interface ErrorSupportProps {
  error?: string | Error;
  errorCode?: string;
  onRetry?: () => void;
}

const bottomMenu = [
  {
    title: 'Pricing',
    path: '/pricing',
  },
  {
    title: 'Privacy',
    path: 'https://txt2create.com/privacy-policy',
  },
  {
    title: 'Terms Of Use',
    path: 'https://txt2create.com/terms-of-use',
  },
];

// SvgShape Component (copied from Login for consistency)
interface SvgShapeProps {
  width: number;
  height: number;
  rotation?: 'normal' | 'reverse';
  position?: { bottom?: number; left?: number; right?: number; top?: number };
  sx?: SxProps;
}

const SvgShape: React.FC<SvgShapeProps> = ({
  width,
  height,
  rotation = 'normal',
  position,
  sx,
}) => {
  const transform = rotation === 'reverse' ? 'scaleX(-1)' : '';
  return (
    <Box
      sx={{
        position: 'absolute',
        width: `${width}px`,
        height: `${height}px`,
        transform,
        bottom: position?.bottom ?? 'auto',
        left: position?.left ?? 'auto',
        right: position?.right ?? 'auto',
        top: position?.top ?? 'auto',
        ...sx,
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 162 547"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M177 547V-27L0 260L177 547Z" fill={`url(#paint0_linear_9735_50627)`} />
        <defs>
          <linearGradient
            id={'paint0_linear_9735_50627'}
            x1="-419.111"
            y1="365.254"
            x2="161.829"
            y2="365.254"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.2" stopColor="#7B35EF" />
            <stop offset="1" stopColor="#7B35EF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </Box>
  );
};

export const ErrorSupport: React.FC<ErrorSupportProps> = ({ error, errorCode, onRetry }) => {
  const { push } = useRouter();
  const theme = useTheme();
  const [isTroubleshooting, setIsTroubleshooting] = useState(false);
  const [copied, setCopied] = useState(false);

  const isExtraSmall = useMediaQuery(theme.breakpoints.down('lg'));

  // Sanitize error for display and support - hide sensitive info
  const sanitizeError = (err: string | Error): string => {
    const errorStr = err instanceof Error ? err.message : err;
    // Remove potential sensitive patterns like tokens, passwords, etc.
    return errorStr
      .replace(/([?&])(token|password|secret|key)=[^&\s]*/gi, '$1$2=***')
      .replace(/Bearer\s+[^\s]+/gi, 'Bearer ***')
      .replace(/Authorization:\s*[^\s]+/gi, 'Authorization: ***');
  };

  const displayError = error ? sanitizeError(error) : 'An authentication error occurred.';
  const supportError = error ? sanitizeError(error) : 'No error details available.';

  const handleTroubleshoot = async () => {
    setIsTroubleshooting(true);
    try {
      await resetAuthSession({ mode: 'popup' });
      // After troubleshooting, let the user manually retry or navigate
      // Removed automatic onRetry call to prevent quick redirects
    } catch (e) {
      // If redirecting, this won't reach here
      console.error('Troubleshoot failed:', e);
    } finally {
      setIsTroubleshooting(false);
    }
  };

  const handleCopyError = async () => {
    try {
      await navigator.clipboard.writeText(
        `Error Code: ${
          errorCode || 'Unknown'
        }\nError Details: ${supportError}\nTimestamp: ${new Date().toISOString()}\nUser Agent: ${
          navigator.userAgent
        }\nURL: ${window.location.href}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy error:', e);
    }
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent(
      `TXT2create Authentication Error - ${errorCode || 'Unknown'}`
    );
    const body = encodeURIComponent(`Hello TXT2create Support Team,

I encountered an authentication error while using TXT2create. Here are the details:

Error Code: ${errorCode || 'Unknown'}
Error Details: ${supportError}
Timestamp: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
URL: ${window.location.href}

Please help me resolve this issue.

Best regards,
[Your Name]`);

    const mailtoUrl = `mailto:support@txt2create.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      <Grid
        item
        xs={12}
        lg={7}
        display={{ xs: 'none', lg: 'block' }}
        sx={{ overflow: 'hidden', borderTopRightRadius: 30, borderBottomRightRadius: 30 }}
      >
        <video
          src={UPLOADED_PUBLIC_ASSETS_URL + LOGIN_SCREEN_VIDEO}
          height={'100%'}
          width={'100%'}
          style={{ objectFit: 'cover' }}
          autoPlay
          loop
          muted
        />
      </Grid>

      <Grid
        item
        xs={12}
        lg={5}
        sx={{
          height: '100%',
          position: 'relative',
          background: 'none',
          backgroundSize: 'cover',
          backgroundPositionX: '50% !important',
          backgroundPositionY: '-80px !important',
          overflow: 'hidden',
          backgroundRepeat: 'no-repeat !important',
        }}
      >
        <SvgShape
          width={162}
          height={547}
          rotation="normal"
          position={{ right: 0 }}
          sx={{
            opacity: 1,
          }}
        />
        <SvgShape
          width={162}
          height={547}
          rotation="reverse"
          position={{}}
          sx={{
            opacity: { xs: 0, sm: 1 },
            bottom: { xs: -40, sm: -240, lg: -40 },
            left: -45,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: { xs: '100%', sm: '75%' },
            marginX: 'auto',
          }}
        >
          <Stack sx={style.loginContainer} spacing={2} alignItems="center">
            <Stack
              sx={{
                position: { xs: 'absolute', lg: 'static' },
                top: { xs: '0px', sm: '-50px', md: '0' },
                left: { xs: '16px', sm: '16px', lg: '0' },
                ml: { xs: '16px', sm: 0 },
              }}
            >
              <NextImage
                src="/assets/logoWithText.svg"
                alt="Logo"
                width={264}
                height={51}
                style={{ borderRadius: 0 }}
                sx={{ width: { xs: 130, sm: 150, md: 264 } }}
              />
            </Stack>

            <Stack pt={2} spacing={3} alignItems="center">
              <Typography
                fontWeight={600}
                mt={2}
                sx={{
                  fontSize: { xs: 40, lg: 28 },
                  lineHeight: { xs: '44px', lg: '36px' },
                  textAlign: 'center',
                }}
              >
                Authentication Error
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: (theme) => theme.palette.background.activeBorder,
                  maxWidth: { xs: '100%', lg: 400 },
                  textAlign: 'center',
                  fontSize: { xs: 16, sm: 18 },
                }}
              >
                We're sorry, but we encountered an issue with your authentication. This might be due
                to a session timeout, network connectivity, or a temporary service issue.
              </Typography>

              {errorCode && (
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.error.main,
                    fontWeight: 500,
                  }}
                >
                  Error Code: {errorCode}
                </Typography>
              )}

              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 2,
                  maxWidth: { xs: '100%', lg: 400 },
                  width: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-word',
                  }}
                >
                  {displayError}
                </Typography>
              </Box>

              <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 300 }}>
                <LoadingButton
                  variant="contained"
                  sx={style.loginBtn}
                  loading={isTroubleshooting}
                  onClick={handleTroubleshoot}
                  fullWidth
                >
                  Troubleshoot & Retry
                </LoadingButton>

                {/* New: Back to Login button - prominent, consistent styling with login button */}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => push('/?mode=login')}
                  sx={{
                    ...style.loginBtn,
                    backgroundColor: '#6A2EF8',
                    boxShadow: 'none',
                    mt: 1,
                  }}
                  fullWidth
                  aria-label="Back to login"
                >
                  Back to Login
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  If the issue persists, please contact our support team for assistance.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCopyError}
                    startIcon={<Iconify icon="mdi:content-copy" />}
                    fullWidth
                  >
                    {copied ? 'Copied!' : 'Copy Error Details'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleEmailSupport}
                    startIcon={<Iconify icon="mdi:email" />}
                    fullWidth
                  >
                    Email Support
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  mt: 4,
                  alignItems: 'center',
                  position: { xs: 'absolute', sm: 'relative' },
                  bottom: 0,
                }}
              >
                {bottomMenu.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        pr: 2,
                        pl: index > 0 ? 2 : 0,
                        cursor: 'pointer',
                        color: 'white',
                        textAlign: 'left',
                        fontSize: 13,
                        '&:hover': {
                          color: (theme) => theme.palette.background.activeBorder,
                        },
                      }}
                      onClick={() => push(item.path)}
                    >
                      {item.title}
                    </Typography>
                    {index !== bottomMenu.length - 1 && (
                      <Divider orientation="vertical" flexItem sx={{ bgcolor: 'gray' }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
};

const styles: Styles = {};
